import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { useSortableData, SortDirection } from '../components/SortableTable';

interface Metric {
  id: number;
  name: string;
  endpoint: string;
  active: boolean;
  timelineAvailable: boolean;
  benchmarkAvailable: boolean;
  withComparison: boolean;
  group: string;
  permissionCategory?: string;
  permissionCapability?: string;
  policyFeature?: string;
  units: string;
}

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const CreateButton = styled(Link)`
  padding: 10px 20px;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  text-decoration: none;
  &:hover { background: #4b5563; color: white; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid #e5e7eb;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#374151')};
  border-bottom: 2px solid #e5e7eb;
  background: #f9fafb;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
  }
`;

const Td = styled.td`
  padding: 12px;
  font-size: 14px;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
`;

const MetricLink = styled(Link)`
  color: #d97706;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

function AdminMetricsPage() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [loading, setLoading] = useState(true);
  
  const { sortedData, requestSort, getSortDirection } = useSortableData(metrics, { key: 'name', direction: 'asc' });

  const fetchMetrics = useCallback(async () => {
    try {
      const res = await api.get('/admin2/metrics.json');
      setMetrics(res.data.metrics || []);
    } catch (err) {
      console.error('Failed to load metrics', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  if (loading) {
    return (
      <AdminLayout title="Metrics" pageTitle="Metrics">
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Metrics" pageTitle="Metrics">
      <PageHeader>
        <PageTitle>Metrics</PageTitle>
        <CreateButton to="/admin2/metrics/new">Create a metric</CreateButton>
      </PageHeader>

      <Table>
        <thead>
          <tr>
            <Th $sortable $sorted={getSortDirection('name') !== null} onClick={() => requestSort('name')}>
              Name <SortIndicator $direction={getSortDirection('name')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('endpoint') !== null} onClick={() => requestSort('endpoint')}>
              Endpoint <SortIndicator $direction={getSortDirection('endpoint')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('timelineAvailable') !== null} onClick={() => requestSort('timelineAvailable')}>
              Timeline available <SortIndicator $direction={getSortDirection('timelineAvailable')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('group') !== null} onClick={() => requestSort('group')}>
              Group <SortIndicator $direction={getSortDirection('group')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('permissionCategory') !== null} onClick={() => requestSort('permissionCategory')}>
              Permission category <SortIndicator $direction={getSortDirection('permissionCategory')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('permissionCapability') !== null} onClick={() => requestSort('permissionCapability')}>
              Permission capability <SortIndicator $direction={getSortDirection('permissionCapability')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('active') !== null} onClick={() => requestSort('active')}>
              Active <SortIndicator $direction={getSortDirection('active')} />
            </Th>
            <Th $sortable $sorted={getSortDirection('units') !== null} onClick={() => requestSort('units')}>
              Units <SortIndicator $direction={getSortDirection('units')} />
            </Th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((metric) => (
            <tr key={metric.id}>
              <Td>
                <MetricLink to={`/admin2/metrics/${metric.id}/edit`}>{metric.name}</MetricLink>
              </Td>
              <Td>{metric.endpoint}</Td>
              <Td>{metric.timelineAvailable ? 'true' : 'false'}</Td>
              <Td>{metric.group}</Td>
              <Td>{metric.permissionCategory || ''}</Td>
              <Td>{metric.permissionCapability || ''}</Td>
              <Td>{metric.active ? 'true' : 'false'}</Td>
              <Td>{metric.units}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </AdminLayout>
  );
}

export default AdminMetricsPage;
